import type { ReactNode } from 'react';

import Skeleton from '@/components/Skeleton/Skeleton';
import useSaunaSessionHistory from '@/features/homeAutomation/hooks/useSaunaSessionHistory';

import SaunaHeatingRateChart from './SaunaHeatingRateChart';
import SaunaSessionList from './SaunaSessionList';
import SaunaSetupNotes from './SaunaSetupNotes';

export interface SaunaSessionHistorySectionProps {
  homeId: string;
  enabled: boolean;
}

/** Session history (heating rate trend + session list) and setup notes for the sauna detail modal. */
export default function SaunaSessionHistorySection({ homeId, enabled }: SaunaSessionHistorySectionProps) {
  const history = useSaunaSessionHistory(homeId, enabled);

  if (history.isPending) {
    return (
      <div className="flex flex-col gap-2" aria-busy="true" aria-label="Loading session history">
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  if (history.isError) {
    return (
      <p role="alert" className="text-sm text-(--accent-danger)">
        Failed to load session history. {history.error.message}
      </p>
    );
  }

  const { sessions, setupNotes, windowDays, generatedAt, truncated } = history.data;

  return (
    <div className="flex flex-col gap-5">
      <Section title="Heating rate trend">
        <SaunaHeatingRateChart sessions={sessions} setupNotes={setupNotes} windowDays={windowDays} generatedAt={generatedAt} />
      </Section>
      <Section title={`Sessions (last ${windowDays} days)`}>
        <SaunaSessionList sessions={sessions} truncated={truncated} />
      </Section>
      <Section title="Setup notes">
        <SaunaSetupNotes homeId={homeId} notes={setupNotes} />
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-sm font-semibold text-(--text-primary)">{title}</h3>
      {children}
    </section>
  );
}
